import apiClient from './client';
import { format } from 'date-fns';

/**
 * 캘린더 관련 API 함수
 */

/**
 * 프로젝트 캘린더 응답 타입
 */
export interface ProjectCalendarResponse {
  id: number;
  name: string;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  colorCode: string | null;
  status: string | null; // ProjectStatus enum 값
}

/**
 * 일정 캘린더 응답 타입
 */
export interface ScheduleCalendarResponse {
  scheduleId: number;
  title: string;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  type: string | null; // ScheduleType enum 값
}

/**
 * 프로젝트 + 일정 캘린더 응답 타입
 */
export interface ProjectCalendarWithSchedulesResponse {
  project: ProjectCalendarResponse;
  schedules: ScheduleCalendarResponse[];
}

/**
 * 기간별 프로젝트 조회 (프로젝트만)
 * @param startDate 시작일
 * @param endDate 종료일
 * @returns 프로젝트 목록
 */
export const getProjectsByPeriod = async (
  startDate: Date,
  endDate: Date
): Promise<ProjectCalendarResponse[]> => {
  const startDateStr = format(startDate, 'yyyy-MM-dd');
  const endDateStr = format(endDate, 'yyyy-MM-dd');
  
  const response = await apiClient.get<ProjectCalendarResponse[]>('/projects/period', {
    params: {
      startDate: startDateStr,
      endDate: endDateStr,
    },
  });
  
  return response.data;
};

/**
 * 기간별 프로젝트 + 일정 조회
 * @param startDate 시작일
 * @param endDate 종료일
 * @returns 프로젝트 + 일정 목록
 */
export const getProjectsByPeriodWithSchedules = async (
  startDate: Date,
  endDate: Date
): Promise<ProjectCalendarWithSchedulesResponse[]> => {
  const startDateStr = format(startDate, 'yyyy-MM-dd');
  const endDateStr = format(endDate, 'yyyy-MM-dd');
  
  const response = await apiClient.get<ProjectCalendarWithSchedulesResponse[]>(
    '/projects/period/with-schedules',
    {
      params: {
        startDate: startDateStr,
        endDate: endDateStr,
      },
    }
  );
  
  return response.data;
};

/**
 * 기간별 기본 일정 조회 (프로젝트에 속하지 않은 일정)
 * @param startDate 시작일
 * @param endDate 종료일
 * @returns 기본 일정 목록
 */
export const getSchedulesByPeriod = async (
  startDate: Date,
  endDate: Date
): Promise<ScheduleCalendarResponse[]> => {
  const startDateStr = format(startDate, 'yyyy-MM-dd');
  const endDateStr = format(endDate, 'yyyy-MM-dd');
  
  const response = await apiClient.get<ScheduleCalendarResponse[]>('/schedules/period', {
    params: {
      startDate: startDateStr,
      endDate: endDateStr,
    },
  });
  
  return response.data;
};

/**
 * My Tasks 응답 타입
 */
export interface MyTaskResponse {
  scheduleId: number;
  scheduleTitle: string;
  projectTitle: string;
  scheduleStartDate: string; // ISO date string
  scheduleEndDate: string; // ISO date string
  colorCode: string | null;
  scheduleType: string; // ScheduleType enum 값
}

/**
 * 프로젝트별 그룹화된 할 일 응답 타입
 */
export interface ProjectTaskGroup {
  projectId: number;
  projectTitle: string;
  colorCode: string | null;
  tasks: MyTaskResponse[];
}

/**
 * 이번달 내 할 일 조회
 * @param startDate 시작일
 * @param endDate 종료일
 * @returns 프로젝트별로 그룹화된 할 일 목록
 */
export const getMyTasks = async (
  startDate: Date,
  endDate: Date
): Promise<ProjectTaskGroup[]> => {
  const startDateStr = format(startDate, 'yyyy-MM-dd');
  const endDateStr = format(endDate, 'yyyy-MM-dd');
  
  const response = await apiClient.get<ProjectTaskGroup[]>('/schedules/my-tasks', {
    params: {
      startDate: startDateStr,
      endDate: endDateStr,
    },
  });
  
  return response.data;
};

/**
 * 팀원 오늘 일정 응답 타입
 */
export interface TodayTeamTaskResponse {
  memberName: string;
  scheduleTitle: string;
  projectTitle: string;
  projectColorCode: string | null;
}

/**
 * 팀원별 그룹화된 오늘 일정 응답 타입
 */
export interface TodayTeamTaskGroup {
  userId: number;
  memberName: string;
  tasks: TodayTeamTaskResponse[];
}

/**
 * 팀원 오늘 일정 조회
 * @param date 조회할 날짜
 * @returns 팀원별로 그룹화된 오늘 일정 목록
 */
export const getTodayTeamTasks = async (
  date: Date
): Promise<TodayTeamTaskGroup[]> => {
  const dateStr = format(date, 'yyyy-MM-dd');
  
  const response = await apiClient.get<TodayTeamTaskGroup[]>('/users/team-tasks', {
    params: {
      date: dateStr,
    },
  });
  
  return response.data;
};

